'use client'

import Link from 'next/link'
import {
  formatChangeBadge,
  getInstrumentMeta,
  type MarketInstrumentKey,
  type MarketRateSnapshot,
} from '@/lib/market-rates'
import MarketInstrumentLabel from '@/components/market/MarketInstrumentLabel'
import MarketRateValue from '@/components/market/MarketRateValue'

type MarketRateCardProps = {
  instrumentKey: MarketInstrumentKey
  row?: MarketRateSnapshot | null
  busy?: boolean
  linked?: boolean
  variant?: 'strip' | 'compact' | 'hero'
  className?: string
}

export default function MarketRateCard({
  instrumentKey,
  row,
  busy = false,
  linked = true,
  variant = 'strip',
  className = '',
}: MarketRateCardProps) {
  const meta = getInstrumentMeta(instrumentKey)
  const badge = formatChangeBadge(row?.changePct)

  const content =
    variant === 'hero' ? (
      <div className={`rounded-xl border border-gray-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950 ${className}`}>
        <MarketInstrumentLabel instrumentKey={instrumentKey} size="sm" />
        <div className="mt-2 flex flex-wrap items-end gap-3">
          <MarketRateValue
            instrumentKey={instrumentKey}
            rate={row?.rate}
            decimals={meta.decimals}
            busy={busy}
            size="lg"
          />
          <span
            className={`market-change-badge inline-flex shrink-0 whitespace-nowrap rounded px-2 py-1 text-xs font-bold text-white ${badge.className}`}
            style={{ color: '#ffffff' }}
          >
            {badge.text}
          </span>
        </div>
      </div>
    ) : (
      <div
        className={
          variant === 'compact'
            ? `flex min-w-[4.5rem] flex-col justify-center gap-0.5 rounded-md px-2 py-1.5 sm:min-w-[5rem] ${className}`
            : `flex min-w-[5rem] flex-col justify-center gap-0.5 rounded-md px-2 py-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-slate-800 sm:min-w-[5.5rem] sm:px-2.5 sm:py-2 ${className}`
        }
      >
        <MarketInstrumentLabel instrumentKey={instrumentKey} size="xs" />
        <div className="flex items-center gap-1">
          <MarketRateValue
            instrumentKey={instrumentKey}
            rate={row?.rate}
            decimals={meta.decimals}
            busy={busy}
            size="sm"
          />
          <span
            className={`market-change-badge inline-flex shrink-0 whitespace-nowrap rounded px-1 py-0.5 text-[9px] font-bold text-white sm:text-[10px] ${badge.className}`}
            style={{ color: '#ffffff' }}
          >
            {badge.text}
          </span>
        </div>
      </div>
    )

  if (!linked) return content

  return (
    <Link href={meta.href} className={variant === 'hero' ? 'block' : undefined}>
      {content}
    </Link>
  )
}
